import React from 'react';
import Footer from '@/components/Footer';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Zap, Heart, Sparkles, ExternalLink, Mail, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

const About = () => {
  return (
    <div className="min-h-screen bg-stone-50 flex flex-col font-sans">
      {/* Header */}
      <header className="bg-stone-50/90 backdrop-blur-sm border-b border-stone-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 sm:gap-2.5 hover:opacity-75 transition-opacity duration-150 flex-shrink-0">
              <img 
                src="/icon-192x192.png" 
                alt="PGastos" 
                className="h-7 sm:h-8 w-auto"
              />
              <span className="text-base sm:text-lg font-bold text-stone-900 tracking-tight">PGastos</span>
            </Link>
            <Link to="/">
              <Button variant="outline" size="sm" className="border-stone-300 hover:bg-stone-100 text-stone-700 text-xs sm:text-sm active:scale-95 transition-all duration-150 ease-spring cursor-pointer">
                <ArrowLeft className="w-4 h-4 mr-1.5" />
                Volver
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          {/* Hero Section */}
          <div className="text-center mb-14">
            <p className="text-xs font-semibold uppercase tracking-widest text-stone-400 mb-3">Empresa</p>
            <h1 className="text-3xl sm:text-5xl font-black text-stone-900 mb-4 tracking-tight">
              Acerca de <span className="text-indigo-600">PGastos</span>
            </h1>
            <p className="text-base sm:text-lg text-stone-600 max-w-xl mx-auto font-medium leading-relaxed">
              Herramienta enfocada en darte control y visibilidad financiera sin fricción ni complicaciones.
            </p>
          </div>

          {/* Mission */}
          <Card className="bg-white rounded-3xl border border-stone-200 shadow-sm p-7 sm:p-10 mb-8">
            <h2 className="text-2xl font-black text-stone-900 mb-4 tracking-tight">Nuestra Misión</h2>
            <p className="text-sm sm:text-base text-stone-600 leading-relaxed mb-4 font-medium">
              En PGastos creemos que el orden financiero personal no debe requerir hojas de cálculo eternas ni procesos complejos. Nuestra misión es construir una herramienta rápida, privada y directa que te permita saber exactamente dónde estás parado cada día.
            </p>
            <p className="text-sm sm:text-base text-stone-600 leading-relaxed font-medium">
              Aprovechamos la tecnología de transcripción e inteligencia artificial aplicada para resolver el obstáculo #1 del registro: la pereza de capturar números a mano mientras estás en la calle.
            </p>
          </Card>

          {/* Values */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-white rounded-2xl border border-stone-200 shadow-xs p-6 text-center hover:shadow-sm transition-all duration-200 ease-spring">
              <div className="w-12 h-12 bg-stone-900 text-white rounded-xl flex items-center justify-center mx-auto mb-4">
                <Zap className="w-6 h-6 text-indigo-400" />
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-2">Simplicidad Extrema</h3>
              <p className="text-xs sm:text-sm text-stone-500 leading-relaxed font-medium">
                Sin laberintos de menús. Dictas o anotas tu gasto en 3 segundos y continúas con tu día.
              </p>
            </Card>

            <Card className="bg-white rounded-2xl border border-stone-200 shadow-xs p-6 text-center hover:shadow-sm transition-all duration-200 ease-spring">
              <div className="w-12 h-12 bg-stone-900 text-white rounded-xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-6 h-6 text-emerald-400" />
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-2">Privacidad Real</h3>
              <p className="text-xs sm:text-sm text-stone-500 leading-relaxed font-medium">
                Cero acceso a tus contraseñas bancarias. Tu dinero nunca pasa por nosotros y tus datos están cifrados.
              </p>
            </Card>

            <Card className="bg-white rounded-2xl border border-stone-200 shadow-xs p-6 text-center hover:shadow-sm transition-all duration-200 ease-spring">
              <div className="w-12 h-12 bg-stone-900 text-white rounded-xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-6 h-6 text-amber-400" />
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-2">Enfoque Mexicano</h3>
              <p className="text-xs sm:text-sm text-stone-500 leading-relaxed font-medium">
                Diseñado para la realidad local: compras a MSI, ligas de pago SPEI con CLABE y control de tarjetas.
              </p>
            </Card>
          </div>

          {/* Team / VJRDEV */}
          <Card className="bg-white rounded-3xl border border-stone-200 shadow-sm p-7 sm:p-10 mb-8 text-center">
            <h2 className="text-2xl font-black text-stone-900 mb-2 tracking-tight">Desarrollado por VJRDEV Company</h2>
            <p className="text-sm text-stone-500 max-w-md mx-auto mb-6 font-medium">
              Estudio de software independiente dedicado a crear herramientas digitales confiables y bien diseñadas.
            </p>
            <a 
              href="https://vjrodriguez.dev" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-stone-900 hover:bg-stone-800 text-white font-bold text-xs sm:text-sm rounded-xl active:scale-95 hover:scale-[1.02] transition-all duration-150 ease-spring shadow-sm"
            >
              <span>Conoce más en vjrodriguez.dev</span>
              <ExternalLink className="w-4 h-4" />
            </a>
          </Card>

          {/* Contact CTA */}
          <div className="bg-stone-900 rounded-3xl border border-stone-800 p-8 sm:p-12 text-center text-white shadow-xl">
            <h2 className="text-2xl sm:text-3xl font-black mb-3 tracking-tight">¿Tienes alguna pregunta o propuesta?</h2>
            <p className="text-sm sm:text-base text-stone-400 mb-6 max-w-md mx-auto font-medium">
              Escríbenos directamente y te responderemos lo más pronto posible.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-sm px-8 py-5 rounded-2xl active:scale-95 hover:scale-[1.02] transition-all duration-150 ease-spring cursor-pointer shadow-md">
                <Mail className="w-4 h-4 mr-2" />
                Contactar a Soporte
              </Button>
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default About;
